import { useRef, useEffect, useCallback } from 'react';

const SCORE_WEIGHTS = {
  'tab-switch': (e) => 25 + (e.duration > 10000 ? 15 : 0),
  'app-blur': (e) => 15 + (e.duration > 10000 ? 10 : 0),
  'mouse-leave': (e) => (e.duration > 5000 ? 5 : 0),
  'paste': () => 25,
  'keyboard-shortcut': (e) => (e.detail === 'Ctrl+V' ? 15 : 0),
  'devtools-detected': () => 40,
};

function calculateScore(events) {
  return events.reduce((total, e) => {
    const weight = SCORE_WEIGHTS[e.type];
    return total + (weight ? weight(e) : 0);
  }, 0);
}

const useAntiCheat = () => {
  const eventsRef = useRef([]);
  const isTrackingRef = useRef(false);
  const tabHideTimeRef = useRef(null);
  const blurTimeRef = useRef(null);
  const mouseLeaveTimeRef = useRef(null);
  const devToolsIntervalRef = useRef(null);

  const addEvent = useCallback((event) => {
    if (!isTrackingRef.current) return;
    eventsRef.current.push({ ...event, timestamp: Date.now() });
  }, []);

  const startTracking = useCallback(() => {
    eventsRef.current = [];
    tabHideTimeRef.current = null;
    blurTimeRef.current = null;
    mouseLeaveTimeRef.current = null;
    isTrackingRef.current = true;
  }, []);

  const stopTracking = useCallback(() => {
    isTrackingRef.current = false;
  }, []);

  const getAntiCheatData = useCallback(() => {
    const now = Date.now();
    const events = [...eventsRef.current];

    if (tabHideTimeRef.current) {
      events.push({ type: 'tab-switch', timestamp: tabHideTimeRef.current, duration: now - tabHideTimeRef.current });
    }
    if (blurTimeRef.current) {
      const dur = now - blurTimeRef.current;
      if (dur > 500) {
        events.push({ type: 'app-blur', timestamp: blurTimeRef.current, duration: dur });
      }
    }

    const score = calculateScore(events);
    const verdict = score >= 30 ? 'suspicious' : 'honest';
    return { events, score, verdict };
  }, []);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        if (isTrackingRef.current) tabHideTimeRef.current = Date.now();
      } else {
        if (tabHideTimeRef.current) {
          const duration = Date.now() - tabHideTimeRef.current;
          addEvent({ type: 'tab-switch', duration });
          tabHideTimeRef.current = null;
        }
      }
    };

    const handleBlur = () => {
      if (isTrackingRef.current) blurTimeRef.current = Date.now();
    };

    const handleFocus = () => {
      if (blurTimeRef.current) {
        const duration = Date.now() - blurTimeRef.current;
        if (duration > 500) addEvent({ type: 'app-blur', duration });
        blurTimeRef.current = null;
      }
    };

    const handleMouseLeave = (e) => {
      if (e.target === document.documentElement && isTrackingRef.current) {
        mouseLeaveTimeRef.current = Date.now();
      }
    };

    const handleMouseEnter = () => {
      if (mouseLeaveTimeRef.current) {
        const duration = Date.now() - mouseLeaveTimeRef.current;
        if (duration > 1000) addEvent({ type: 'mouse-leave', duration });
        mouseLeaveTimeRef.current = null;
      }
    };

    const handlePaste = (e) => {
      const text = e.clipboardData?.getData('text') || '';
      addEvent({ type: 'paste', detail: text.length });
    };

    const handleCopy = () => {
      addEvent({ type: 'copy' });
    };

    const handleKeyDown = (e) => {
      if (e.ctrlKey || e.metaKey) {
        if (e.key === 'v') addEvent({ type: 'keyboard-shortcut', detail: 'Ctrl+V' });
        else if (e.key === 'c') addEvent({ type: 'keyboard-shortcut', detail: 'Ctrl+C' });
        else if (e.key === 'a') addEvent({ type: 'keyboard-shortcut', detail: 'Ctrl+A' });
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('blur', handleBlur);
    window.addEventListener('focus', handleFocus);
    document.documentElement.addEventListener('mouseleave', handleMouseLeave);
    document.documentElement.addEventListener('mouseenter', handleMouseEnter);
    document.addEventListener('paste', handlePaste);
    document.addEventListener('copy', handleCopy);
    document.addEventListener('keydown', handleKeyDown);

    devToolsIntervalRef.current = setInterval(() => {
      if (!isTrackingRef.current) return;
      const wDiff = window.outerWidth - window.innerWidth;
      const hDiff = window.outerHeight - window.innerHeight;
      if (wDiff > 160 || hDiff > 160) {
        const recent = eventsRef.current.filter(e => e.type === 'devtools-detected');
        const lastTime = recent.length > 0 ? recent[recent.length - 1].timestamp : 0;
        if (Date.now() - lastTime > 5000) {
          addEvent({ type: 'devtools-detected' });
        }
      }
    }, 2000);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('blur', handleBlur);
      window.removeEventListener('focus', handleFocus);
      document.documentElement.removeEventListener('mouseleave', handleMouseLeave);
      document.documentElement.removeEventListener('mouseenter', handleMouseEnter);
      document.removeEventListener('paste', handlePaste);
      document.removeEventListener('copy', handleCopy);
      document.removeEventListener('keydown', handleKeyDown);
      if (devToolsIntervalRef.current) clearInterval(devToolsIntervalRef.current);
    };
  }, [addEvent]);

  return { startTracking, stopTracking, getAntiCheatData };
};

export default useAntiCheat;
